import { useCallback } from "react";

import { AvailableCaseAction } from "src/cases/caseView/hooks";
import { TaskIdExtractor } from "src/cases/queries/casesTypes";
import {
  useApproveActionFieldHint,
  useApproveActionHint,
  useRejectActionFieldHint,
  useRejectActionHint,
} from "src/cases/queries/hints";
import type {
  ActionTriggeredEventDataActionFormDataValue,
  Value,
} from "src/clients/case-management";

type UseActionFieldHintActionsParams = {
  caseId?: string;
  selectedAction: AvailableCaseAction;
  liveFormEtag?: string | number;
  fieldHints?: Record<string, unknown>;
};

export const useActionFieldHintActions = ({
  caseId,
  selectedAction,
  liveFormEtag,
  fieldHints,
}: UseActionFieldHintActionsParams) => {
  const { mutate: approveActionFieldHint, isPending: isApprovingHint } =
    useApproveActionFieldHint();
  const { mutateAsync: approveActionHint } = useApproveActionHint();
  const { mutate: rejectActionFieldHint, isPending: isRejectingHint } =
    useRejectActionFieldHint();
  const { mutateAsync: rejectActionHint } = useRejectActionHint();

  const handleApproveField = useCallback(
    (fieldKey: string) => {
      const currentEtag = liveFormEtag ?? selectedAction?.form.meta?.etag;
      if (!caseId || currentEtag == null) return;
      const actionKey = TaskIdExtractor.getStatusAndTaskKey(selectedAction.id);
      approveActionFieldHint({
        caseId,
        actionKey,
        fieldKey,
        hintId: selectedAction.id,
        ifMatch: currentEtag.toString(),
        value: (fieldHints?.[fieldKey] ?? null) as Value | null,
      });
    },
    [caseId, liveFormEtag, selectedAction, fieldHints, approveActionFieldHint],
  );

  const handleRejectField = useCallback(
    (fieldKey: string) => {
      if (!caseId || isRejectingHint) return;
      rejectActionFieldHint({
        caseId,
        actionKey: TaskIdExtractor.getStatusAndTaskKey(selectedAction.id),
        fieldKey,
      });
    },
    [caseId, isRejectingHint, selectedAction, rejectActionFieldHint],
  );

  const approveAllHints = useCallback(async () => {
    const currentEtag = liveFormEtag ?? selectedAction?.form.meta?.etag;
    if (!caseId || !fieldHints || currentEtag == null) {
      throw new Error("Cannot approve hints: missing required data");
    }
    const response = await approveActionHint({
      caseId,
      actionKey: TaskIdExtractor.getStatusAndTaskKey(selectedAction.id),
      hintId: selectedAction.id,
      ifMatch: currentEtag.toString(),
      attributes: fieldHints as Record<
        string,
        ActionTriggeredEventDataActionFormDataValue
      >,
    });
    const newEtag = response.data.meta?.etag;
    return newEtag != null ? Number(newEtag) : undefined;
  }, [caseId, fieldHints, selectedAction, liveFormEtag, approveActionHint]);

  const rejectAllHints = useCallback(async () => {
    if (!caseId) return;
    await rejectActionHint({
      caseId,
      actionKey: TaskIdExtractor.getStatusAndTaskKey(selectedAction.id),
    });
  }, [caseId, rejectActionHint, selectedAction.id]);

  return {
    isApprovingHint,
    isRejectingHint,
    handleApproveField,
    handleRejectField,
    approveAllHints,
    rejectAllHints,
  };
};
